import { PERMISSIONS, terraContract } from '@terra/shared-authorization';
import { z } from 'zod';
import {
  GroupApplicationMappingResponseSchema,
  GroupApplicationMappingListResponseSchema,
  SetGroupApplicationMappingSchema,
  UpdateGroupApplicationMappingSchema,
  SsoGroupListResponseSchema,
} from './group-application-mapping.schema';
import { ErrorResponseSchema } from '../user-application-access/user-application-access.schema';

export const GROUP_APPLICATION_MAPPING_PATH = 'group-application-mapping';

export const GroupApplicationMappingContract = terraContract
  .prefix(`/${GROUP_APPLICATION_MAPPING_PATH}`)
  .router({
    listGroupMappings: terraContract
      .meta({
        permissions: [PERMISSIONS.USER_ADMIN.name],
      })
      .route({
        method: 'GET',
        path: '/',
        summary: 'List all group application access mappings',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          query: z.object({
            targetTenantId: z.string().optional(),
          }),
        }),
      )
      .output(GroupApplicationMappingListResponseSchema)
      .errors({ FORBIDDEN: { status: 403, data: ErrorResponseSchema } }),
    listSsoGroups: terraContract
      .meta({
        permissions: [PERMISSIONS.USER_ADMIN.name],
      })
      .route({
        method: 'GET',
        path: '/groups',
        summary: 'List available SSO groups from the identity provider',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          query: z.object({
            targetTenantId: z.string().optional(),
            search: z.string().optional(),
          }),
        }),
      )
      .output(SsoGroupListResponseSchema)
      .errors({ FORBIDDEN: { status: 403, data: ErrorResponseSchema } }),
    getGroupMapping: terraContract
      .meta({
        permissions: [PERMISSIONS.USER_ADMIN.name],
      })
      .route({
        method: 'GET',
        path: '/{groupId}',
        summary: 'Get a specific group application access mapping',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            groupId: z.string(),
          }),
          query: z.object({
            targetTenantId: z.string().optional(),
          }),
        }),
      )
      .output(GroupApplicationMappingResponseSchema)
      .errors({
        FORBIDDEN: { status: 403, data: ErrorResponseSchema },
        NOT_FOUND: { status: 404, data: ErrorResponseSchema },
      }),
    createGroupMapping: terraContract
      .meta({
        permissions: [PERMISSIONS.USER_ADMIN.name],
      })
      .route({
        method: 'POST',
        path: '/',
        summary: 'Create a group application access mapping',
        inputStructure: 'detailed',
        successStatus: 201,
      })
      .input(z.object({ body: SetGroupApplicationMappingSchema }))
      .output(GroupApplicationMappingResponseSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: ErrorResponseSchema },
        FORBIDDEN: { status: 403, data: ErrorResponseSchema },
        CONFLICT: { status: 409, data: ErrorResponseSchema },
        UNPROCESSABLE_CONTENT: { status: 422, data: ErrorResponseSchema },
      }),
    updateGroupMapping: terraContract
      .meta({
        permissions: [PERMISSIONS.USER_ADMIN.name],
      })
      .route({
        method: 'PUT',
        path: '/{groupId}',
        summary: 'Update a group application access mapping',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            groupId: z.string(),
          }),
          body: UpdateGroupApplicationMappingSchema,
        }),
      )
      .output(GroupApplicationMappingResponseSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: ErrorResponseSchema },
        FORBIDDEN: { status: 403, data: ErrorResponseSchema },
        NOT_FOUND: { status: 404, data: ErrorResponseSchema },
        UNPROCESSABLE_CONTENT: { status: 422, data: ErrorResponseSchema },
      }),
    deleteGroupMapping: terraContract
      .meta({
        permissions: [PERMISSIONS.USER_ADMIN.name],
      })
      .route({
        method: 'DELETE',
        path: '/{groupId}',
        summary: 'Delete a group application access mapping',
        inputStructure: 'detailed',
        successStatus: 204,
      })
      .input(
        z.object({
          params: z.object({
            groupId: z.string(),
          }),
          query: z.object({
            targetTenantId: z.string().optional(),
          }),
        }),
      )
      .output(z.null())
      .errors({
        FORBIDDEN: { status: 403, data: ErrorResponseSchema },
        NOT_FOUND: { status: 404, data: ErrorResponseSchema },
      }),
  });
